// Encaminhar mensagem (mockup "Modal Encaminhar A"): citação da mensagem,
// busca e múltiplos destinos — um POST /chats/messages/:id/forward por chat.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import ForwardIcon from "@material-ui/icons/Forward";
import GroupIcon from "@material-ui/icons/Group";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { chatTitle, isGroup, otherUserOf, initialsOf, avatarColorFor, matchesSearch } from "./chatRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    paper: { width: 520, maxWidth: "calc(100vw - 32px)", margin: 16, maxHeight: "calc(var(--vh-full) - 32px)" },
    body: { padding: "16px 24px 8px", gap: 0 },
    quote: { borderLeft: `3px solid #7DD3FC`, backgroundColor: theme.palette.aeros.surface, borderRadius: 8, padding: "8px 12px", marginBottom: 14 },
    quoteName: { fontSize: 11, fontWeight: 700, color: "#7DD3FC" },
    quoteText: { fontSize: 12.5, color: theme.palette.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    search: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`, borderRadius: 10, padding: "4px 12px", fontSize: 13, color: theme.palette.text.primary, display: "flex", gap: 8, alignItems: "center", width: "100%", marginBottom: 6, "& .MuiSvgIcon-root": { fontSize: 17, color: theme.palette.text.secondary } },
    list: { display: "flex", flexDirection: "column", maxHeight: 280, overflowY: "auto", ...theme.scrollbarStyles },
    row: { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, cursor: "pointer", background: "none", border: "none", font: "inherit", color: "inherit", textAlign: "left", width: "100%", "&:hover": { backgroundColor: "rgba(160,68,255,0.06)" } },
    rowOn: { backgroundColor: "rgba(160,68,255,0.08)" },
    checkbox: { width: 16, height: 16, borderRadius: 4, border: "1.5px solid #5B6376", display: "flex", alignItems: "center", justifyContent: "center", color: "#07080D", fontSize: 10, fontWeight: 800, flex: "none" },
    checkboxOn: { borderColor: "transparent", background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})` },
    grpAv: { width: 30, height: 30, borderRadius: 9, backgroundColor: "rgba(160,68,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: stateText(theme, "#C08BFF", "purple"), "& .MuiSvgIcon-root": { fontSize: 15 } },
    dmAv: { width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#07080D" },
    name: { flex: 1, fontSize: 13, fontWeight: 600, color: theme.palette.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    sub: { fontSize: 11, color: theme.palette.aeros.textTertiary },
    count: { fontSize: 12, color: theme.palette.text.secondary },
  };
});

export default function ForwardModal({ open, message, chats, onlineUsers, onClose, onDone }) {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const { user: me } = useContext(AuthContext);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const online = useMemo(() => new Set((onlineUsers || []).filter((u) => u.online).map((u) => Number(u.id))), [onlineUsers]);

  useEffect(() => {
    if (open) {
      setSelected([]);
      setSearch("");
    }
  }, [open]);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const list = (chats || []).filter((c) => matchesSearch(c, search, me.id));

  const send = async () => {
    setSending(true);
    try {
      for (const targetChatId of selected) {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/chats/messages/${message.id}/forward`, { targetChatId });
      }
      toast.success(i18n.t("chatMessages.forwardedTo", { count: selected.length }));
      if (onDone) onDone();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  if (!message) return null;

  return (
    <Dialog open={Boolean(open)} onClose={sending ? undefined : onClose} maxWidth={false} classes={{ paper: classes.paper }} scroll="paper">
      <div className={m.form}>
        <div className={m.header}>
          <div>
            <div className={m.title}>{i18n.t("chatMessages.forwardMessage")}</div>
            <div className={m.subtitle}>{i18n.t("chatMessages.forwardSubtitle")}</div>
          </div>
          <IconButton className={m.closeButton} onClick={onClose} disabled={sending} aria-label="Fechar"><CloseIcon /></IconButton>
        </div>
        <div className={`${m.body} ${classes.body}`}>
          <div className={classes.quote}>
            <div className={classes.quoteName}>{message.sender?.name || "Mensagem"}</div>
            <div className={classes.quoteText}>{message.message || message.mediaName || "…"}</div>
          </div>
          <InputBase className={classes.search} placeholder={i18n.t("chatIndex.search")} value={search} onChange={(e) => setSearch(e.target.value)} startAdornment={<SearchIcon />} />
          <div className={classes.list}>
            {list.map((c) => {
              const on = selected.includes(c.id);
              const grp = isGroup(c);
              const other = otherUserOf(c, me.id);
              return (
                <button type="button" key={c.id} className={`${classes.row} ${on ? classes.rowOn : ""}`} onClick={() => toggle(c.id)}>
                  <span className={`${classes.checkbox} ${on ? classes.checkboxOn : ""}`}>{on ? "✓" : ""}</span>
                  {grp ? <span className={classes.grpAv}><GroupIcon /></span> : <span className={classes.dmAv} style={{ backgroundColor: avatarColorFor(other?.id) }}>{initialsOf(other?.name)}</span>}
                  <span className={classes.name}>{chatTitle(c, me.id)}</span>
                  <span className={classes.sub}>{grp ? `Grupo · ${(c.users || []).length}` : online.has(Number(other?.id)) ? "Online" : "Offline"}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className={m.footer}>
          <span className={classes.count}>{i18n.t("chatMessages.selectedCount", { count: selected.length })}</span>
          <div className={m.footerActions}>
            <Button variant="outlined" className={m.cancelButton} onClick={onClose} disabled={sending}>{i18n.t("chatIndex.cancel")}</Button>
            <Button variant="contained" color="primary" className={m.submitButton} startIcon={<ForwardIcon />} onClick={send} disabled={sending || selected.length === 0}>
              {i18n.t("chatMessages.forward")}
              {sending && <CircularProgress size={22} className={m.buttonProgress} />}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
